import type { SignalRow } from '@shared/flow/signal-view';
import { isCallTask } from '../signals/isCallTask';
import { hasPhone } from './hasPhone';

/** The row fields the dialable rule reads (a subset of `SignalRow`, so partial fixtures and the table's row type both fit). */
export type DialableSignalInput = Pick<SignalRow, 'signal_id'> &
  Partial<Pick<SignalRow, 'contact_id' | 'signal_tactic' | 'action_state' | 'contact_best_phone' | 'contact_do_not_call'>>;

/** Why a selected row is left out of the dial, first matching reason wins (so the counts add up to the skipped total). */
export interface SkippedDialCounts {
  /** Not a call task (email step, automated step, …). */
  notCall: number;
  /** A call task whose contact is marked do-not-call. */
  dnc: number;
  /** A call task with no phone number on its contact. */
  noPhone: number;
  /** A call task with no phone whose contact already has a phone-find in flight (held out, own reason). */
  finding: number;
  /** A call task that is not an open To do (snoozed, completed, archived). */
  other: number;
}

export interface DialableSelection {
  /** Selected ids Trellus may dial, in the order the rows were given. */
  dialableIds: string[];
  /** Contacts a phone-find could run for (no phone, not DNC, not already finding; Signals: call task in To do), de-duplicated. */
  enrichableContactIds: string[];
  skipped: SkippedDialCounts;
  /** Selected rows found in `rows` (ids with no loaded row are ignored, not counted as skipped). */
  selectedCount: number;
}

/** The "nothing checked" partition; shared, never mutated. */
export const EMPTY_DIALABLE: DialableSelection = { dialableIds: [], enrichableContactIds: [], skipped: { notCall: 0, dnc: 0, noPhone: 0, finding: 0, other: 0 }, selectedCount: 0 };

export function skippedTotal(skipped: SkippedDialCounts): number {
  return skipped.notCall + skipped.dnc + skipped.noPhone + skipped.finding + skipped.other;
}

/**
 * THE dialable-subset rule for the Signals bulk dial (Spencer, 10/26): Trellus rejects a selection that mixes
 * dialable and non-dialable rows (`mixed_selection`), so Terra publishes only the rows it can dial and tells the rep
 * about the rest. A selected row is dialable when it is a call task (`isCallTask`, the same predicate the group
 * checkbox uses), its contact has a phone and is not do-not-call, and the task is an open `To do`.
 *
 * A call task in To do with no phone, not DNC, and a `contact_id` is also `enrichable` (a phone-find can fill it in).
 */
export function partitionDialableSignals(
  rows: readonly DialableSignalInput[],
  selectedIds: ReadonlySet<string>,
): DialableSelection {
  if (selectedIds.size === 0) return EMPTY_DIALABLE;
  const dialableIds: string[] = [];
  const skipped: SkippedDialCounts = { notCall: 0, dnc: 0, noPhone: 0, finding: 0, other: 0 };
  const enrichable = new Set<string>();
  const seen = new Set<string>();
  let selectedCount = 0;
  for (const row of rows) {
    // Overlapping pages can repeat a row; Trellus rejects duplicate ids.
    if (!selectedIds.has(row.signal_id) || seen.has(row.signal_id)) continue;
    seen.add(row.signal_id);
    selectedCount += 1;
    if (!isCallTask(row)) skipped.notCall += 1;
    else if (row.contact_do_not_call === true) skipped.dnc += 1;
    else if (!hasPhone(row.contact_best_phone)) {
      skipped.noPhone += 1;
      if (row.contact_id && row.action_state === 'To do') enrichable.add(row.contact_id);
    }
    else if (row.action_state !== 'To do') skipped.other += 1;
    else dialableIds.push(row.signal_id);
  }
  return { dialableIds, enrichableContactIds: [...enrichable], skipped, selectedCount };
}
